<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { useGamesNotificationStore } from '../stores/gamesNotificationStore'
import axios from '../axios.js'

const gamesNotificationStore = useGamesNotificationStore()

const showOverlay = computed(() => gamesNotificationStore.showOverlay)
const games = computed(() => gamesNotificationStore.gamesWithStatus)

// Expanded games and player data
const expandedGames = ref(new Set())
const gamePlayers = ref({})
const loadingPlayers = ref(new Set())

const closeOverlay = () => {
  gamesNotificationStore.closeOverlay()
}

const markAsRead = (gameId) => {
  gamesNotificationStore.markAsRead(gameId)
}

const handleEscape = (event) => {
  if (event.key === 'Escape' && showOverlay.value) {
    closeOverlay()
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleEscape)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleEscape)
})

const formatDateTime = (datetime) => {
  if (!datetime) return 'N/A'
  const date = new Date(datetime)
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getStatusBadge = (game) => {
  if (game.inDatabase && game.rowCount > 0) {
    return {
      text: 'In DB',
      class: 'bg-green-100 text-green-800',
      icon: 'fa-check-circle'
    }
  }
  
  if (game.isScraped) {
    return {
      text: 'Scraped',
      class: 'bg-blue-100 text-blue-800',
      icon: 'fa-download'
    }
  }
  
  return {
    text: 'Not in DB',
    class: 'bg-red-100 text-red-800',
    icon: 'fa-times-circle'
  }
}

const toggleGameExpansion = async (game) => {
  const gameId = game.gameId || game.id
  const isNotInDb = !game.inDatabase || game.rowCount === 0
  
  // Allow expansion for games not in DB (including scraped games)
  if (!isNotInDb) {
    return
  }
  
  if (expandedGames.value.has(gameId)) {
    expandedGames.value.delete(gameId)
  } else {
    expandedGames.value.add(gameId)
    
    // Fetch players if not already loaded
    if (!gamePlayers.value[gameId] && !loadingPlayers.value.has(gameId)) {
      await fetchGamePlayers(gameId)
    }
  }
}

const fetchGamePlayers = async (gameId, forceRescrape = false) => {
  if (loadingPlayers.value.has(gameId)) return
  
  loadingPlayers.value.add(gameId)
  
  try {
    // First, try to get from cache (if not forcing rescrape)
    if (!forceRescrape) {
      try {
        const cachedResponse = await axios.get(`/api/proballers/cached-boxscore/${gameId}`)
        if (cachedResponse.data.success && cachedResponse.data.players && cachedResponse.data.players.length > 0) {
          gamePlayers.value[gameId] = cachedResponse.data.players
          loadingPlayers.value.delete(gameId)
          return
        }
      } catch (cacheError) {
        // Cache miss, continue to scrape
        console.log(`Cache miss for game ${gameId}, scraping...`)
      }
    }
    
    // If rescraping, use the rescrape endpoint
    if (forceRescrape) {
      const rescrapeResponse = await axios.post(`/api/proballers/rescrape-boxscore/${gameId}`)
      if (rescrapeResponse.data.success && rescrapeResponse.data.players && rescrapeResponse.data.players.length > 0) {
        gamePlayers.value[gameId] = rescrapeResponse.data.players
        loadingPlayers.value.delete(gameId)
        return
      }
    }
    
    // Fallback: Use Laravel API proxy endpoint to scrape
    const response = await axios.get(`/api/proballers/boxscore/${gameId}`)
    
    if (response.data.success && response.data.players && response.data.players.length > 0) {
      gamePlayers.value[gameId] = response.data.players
    } else {
      gamePlayers.value[gameId] = []
    }
    
  } catch (error) {
    console.error(`Error fetching/scraping players for game ${gameId}:`, error)
    gamePlayers.value[gameId] = []
  } finally {
    loadingPlayers.value.delete(gameId)
  }
}
</script>

<template>
  <transition
    enter-active-class="transition-all duration-300 ease-out"
    leave-active-class="transition-all duration-300 ease-in"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="showOverlay"
      class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center pt-20"
      @click.self="closeOverlay"
    >
      <div
        class="bg-white rounded-lg shadow-2xl w-full w-full h-[90vh] flex flex-col mx-4"
        @click.stop
      >
        <!-- Header -->
        <div class="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-t-lg">
          <div class="flex items-center gap-3">
            <i class="fas fa-basketball-ball text-xl"></i>
            <div>
              <h2 class="text-lg font-semibold">New Games Available</h2>
              <p class="text-xs opacity-90">{{ games.length }} total game{{ games.length !== 1 ? 's' : '' }}</p>
            </div>
          </div>
          <button
            @click="closeOverlay"
            class="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Games List -->
        <div class="flex-1 overflow-y-auto">
          <div v-if="games.length === 0" class="text-center py-8 text-gray-500">
            <i class="fas fa-inbox text-4xl mb-4"></i>
            <p>No games available</p>
          </div>

          <div v-else class="games-grid-container">
            <!-- Table Header -->
            <div class="games-grid-header">
              <div class="font-semibold text-gray-700">Teams</div>
              <div class="font-semibold text-gray-700">Game ID</div>
              <div class="font-semibold text-gray-700">Date/Time</div>
              <div class="font-semibold text-gray-700">Score</div>
              <div class="font-semibold text-gray-700">League</div>
              <div class="font-semibold text-gray-700">Status</div>
              <div class="font-semibold text-gray-700 text-center">Link</div>
            </div>

            <!-- Table Rows -->
            <div class="games-grid-body">
              <template v-for="game in games" :key="game.gameId || game.id">
              <div
                class="games-grid-row"
                :class="{ 
                  'bg-gray-50': game.read, 
                  'hover:bg-gray-100': !game.read,
                  'bg-blue-50': expandedGames.has(game.gameId || game.id),
                  'cursor-pointer': !game.inDatabase || game.rowCount === 0
                }"
                @click="(!game.inDatabase || game.rowCount === 0) ? toggleGameExpansion(game) : markAsRead(game.gameId)"
              >
                <!-- Teams -->
                <div class="font-semibold text-gray-900">
                  {{ game.team1 || 'Team 1' }} vs {{ game.team2 || 'Team 2' }}
                </div>
                
                <!-- Game ID -->
                <div class="text-gray-600">
                  {{ game.gameId || 'N/A' }}
                </div>
                
                <!-- Date/Time -->
                <div class="text-gray-600">
                  {{ formatDateTime(game.datetime) }}
                </div>
                
                <!-- Score -->
                <div class="text-gray-600">
                  {{ game.score || '-' }}
                </div>
                
                <!-- League -->
                <div class="text-gray-600">
                  {{ game.leagueName || game.league || 'N/A' }}
                </div>
                
                <!-- Status Badge -->
                <div>
                  <span
                    :class="[
                      getStatusBadge(game).class,
                      'px-2 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 whitespace-nowrap',
                      { 
                        'cursor-pointer hover:opacity-80': !game.inDatabase || game.rowCount === 0,
                        'cursor-default': game.inDatabase && game.rowCount > 0
                      }
                    ]"
                    @click.stop="(!game.inDatabase || game.rowCount === 0) && toggleGameExpansion(game)"
                    :title="(!game.inDatabase || game.rowCount === 0) ? 'Click to view players' : ''"
                  >
                    <i :class="['fas', getStatusBadge(game).icon]"></i>
                    {{ getStatusBadge(game).text }}
                    <span v-if="game.inDatabase && game.rowCount > 0" class="ml-1">
                      ({{ game.rowCount }})
                    </span>
                    <span v-if="game.isScraped && (!game.inDatabase || game.rowCount === 0)" class="ml-1 text-xs">
                      (cached)
                    </span>
                    <i 
                      v-if="(!game.inDatabase || game.rowCount === 0) && expandedGames.has(game.gameId || game.id)"
                      class="fas fa-chevron-up ml-1 text-xs"
                    ></i>
                    <i 
                      v-else-if="!game.inDatabase || game.rowCount === 0"
                      class="fas fa-chevron-down ml-1 text-xs"
                    ></i>
                  </span>
                </div>
                
                <!-- Action Button -->
                <div class="text-center">
                  <a
                    v-if="game.gameLink"
                    :href="game.gameLink"
                    target="_blank"
                    class="text-blue-600 hover:text-blue-800 p-1.5 rounded transition-colors inline-block"
                    title="View Game"
                    @click.stop
                  >
                    <i class="fas fa-external-link-alt text-sm"></i>
                  </a>
                  <span v-else class="text-gray-300">-</span>
                </div>
              </div>
              
              <!-- Expanded Player List -->
              <div
                v-if="expandedGames.has(game.gameId || game.id)"
                class="bg-gray-50 border-t border-gray-200 p-4"
                style="grid-column: 1 / -1;"
              >
                <!-- Header with Refresh Button -->
                <div class="flex items-center justify-between mb-3">
                  <h4 class="text-sm font-semibold text-gray-700">Player Statistics</h4>
                  <button
                    @click="fetchGamePlayers(game.gameId || game.id, true)"
                    :disabled="loadingPlayers.has(game.gameId || game.id)"
                    class="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    title="Rescrape boxscore"
                  >
                    <i class="fas fa-sync-alt" :class="{ 'fa-spin': loadingPlayers.has(game.gameId || game.id) }"></i>
                    Refresh
                  </button>
                </div>
                
                <div v-if="loadingPlayers.has(game.gameId || game.id)" class="text-center py-4">
                  <i class="fas fa-spinner fa-spin text-gray-400 text-xl"></i>
                  <p class="text-sm text-gray-500 mt-2">Loading players...</p>
                </div>
                <div v-else-if="gamePlayers[game.gameId || game.id] && gamePlayers[game.gameId || game.id].length > 0" class="players-table-container">
                  <div class="players-table-header">
                    <div>Player</div>
                    <div>Team</div>
                    <div>Min</div>
                    <div>Pts</div>
                    <div>Reb</div>
                    <div>Ast</div>
                    <div>Stl</div>
                    <div>Blk</div>
                    <div>TO</div>
                    <div>FG%</div>
                    <div>3P%</div>
                    <div>FT%</div>
                  </div>
                  <div class="players-table-body">
                    <div
                      v-for="player in gamePlayers[game.gameId || game.id]"
                      :key="player.p_player_id || player.player_id"
                      class="players-table-row"
                    >
                      <div class="font-medium text-gray-900">{{ player.player_name }}</div>
                      <div class="text-gray-600">{{ player.team_name }}</div>
                      <div class="text-gray-600">{{ player.minutes || '-' }}</div>
                      <div class="text-gray-600">{{ player.points || '-' }}</div>
                      <div class="text-gray-600">{{ player.rebounds || player.total_rebounds || '-' }}</div>
                      <div class="text-gray-600">{{ player.assists || '-' }}</div>
                      <div class="text-gray-600">{{ player.steals || '-' }}</div>
                      <div class="text-gray-600">{{ player.blocks || '-' }}</div>
                      <div class="text-gray-600">{{ player.turnovers || '-' }}</div>
                      <div class="text-gray-600">{{ player.field_goal_percentage || '-' }}</div>
                      <div class="text-gray-600">{{ player.three_point_percentage || '-' }}</div>
                      <div class="text-gray-600">{{ player.free_throw_percentage || '-' }}</div>
                    </div>
                  </div>
                </div>
                <div v-else class="text-center py-4 text-gray-500 text-sm">
                  No player data available for this game
                </div>
              </div>
              </template>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="border-t border-gray-200 p-4 bg-gray-50 rounded-b-lg flex items-center justify-between">
          <div class="text-sm text-gray-600">
            <span class="font-medium">{{ games.filter(g => g.inDatabase && g.rowCount > 0).length }}</span>
            games in database
            <span class="mx-2">•</span>
            <span class="font-medium">{{ games.filter(g => !g.inDatabase || g.rowCount === 0).length }}</span>
            games not in database
          </div>
          <button
            @click="closeOverlay"
            class="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<style scoped>
/* Games Grid Layout */
.games-grid-container {
  display: flex;
  flex-direction: column;
}

.games-grid-header {
  display: grid;
  grid-template-columns: 2fr 0.8fr 1.3fr 0.8fr 0.9fr 1.2fr 0.6fr;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background-color: #f9fafb;
  border-bottom: 2px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 10;
}

.games-grid-body {
  display: flex;
  flex-direction: column;
}

.games-grid-row {
  display: grid;
  grid-template-columns: 2fr 0.8fr 1.3fr 0.8fr 0.9fr 1.2fr 0.6fr;
  gap: 1rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  transition: background-color 0.15s ease;
  cursor: pointer;
  align-items: center;
}

.games-grid-row:last-child {
  border-bottom: none;
}

/* Custom scrollbar for games list */
.overflow-y-auto::-webkit-scrollbar {
  width: 8px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #a0aec0;
}

/* Players Table */
.players-table-container {
  display: flex;
  flex-direction: column;
  max-height: 400px;
  overflow-y: auto;
}

.players-table-header {
  display: grid;
  grid-template-columns: 2fr 1.5fr 0.6fr 0.6fr 0.6fr 0.6fr 0.6fr 0.6fr 0.6fr 0.8fr 0.8fr 0.8fr;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background-color: #f3f4f6;
  border-bottom: 1px solid #e5e7eb;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  color: #6b7280;
  position: sticky;
  top: 0;
  z-index: 5;
}

.players-table-body {
  display: flex;
  flex-direction: column;
}

.players-table-row {
  display: grid;
  grid-template-columns: 2fr 1.5fr 0.6fr 0.6fr 0.6fr 0.6fr 0.6fr 0.6fr 0.6fr 0.8fr 0.8fr 0.8fr;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #e5e7eb;
  font-size: 0.875rem;
  align-items: center;
  transition: background-color 0.15s ease;
}

.players-table-row:hover {
  background-color: #f9fafb;
}

.players-table-row:last-child {
  border-bottom: none;
}

/* Custom scrollbar for players table */
.players-table-container::-webkit-scrollbar {
  width: 6px;
}

.players-table-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.players-table-container::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 3px;
}

.players-table-container::-webkit-scrollbar-thumb:hover {
  background: #a0aec0;
}
</style>
